import reverse from 'lodash/reverse';
import { getNetPayoutAmount } from '../../util/payoutAdjustments';
import sortBy from 'lodash/sortBy';

import { types as sdkTypes } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError, isNetworkError } from '../../util/errors';
import * as log from '../../util/log';
import { createPayout, queryTransaction, showStripeAccount } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { INBOX_LAST_TRANSITIONS, isInboxVisibleTransaction } from '../InboxPage/InboxPage.duck';
import {
  getDefaultDashboardDateRange,
  getDashboardQueryDateParams,
  isSameDashboardDateRange,
  normalizeDashboardDateRange,
} from './dateRange';
import {
  filterVisibleTransactions,
  sanitizeTransactionQueryResponse,
} from '../../util/transactionVisibility';
import {
  createParticipantUnavailableError,
  splitTransactionsByParticipantAvailability,
  unavailableParticipantLogData,
} from '../../util/participantAvailability';

const { Money } = sdkTypes;

let latestDashboardRequestId = 0;
const USER_FIELDS = [
  'profile.displayName',
  'profile.abbreviatedName',
  'deleted',
  'banned',
  'state',
  'disabled',
];

export const FETCH_DASHBOARD_REQUEST = 'app/DashboardPage/FETCH_DASHBOARD_REQUEST';
export const FETCH_DASHBOARD_SUCCESS = 'app/DashboardPage/FETCH_DASHBOARD_SUCCESS';
export const FETCH_DASHBOARD_ERROR = 'app/DashboardPage/FETCH_DASHBOARD_ERROR';
export const SET_DASHBOARD_TOTAL_EARNINGS = 'app/DashboardPage/SET_DASHBOARD_TOTAL_EARNINGS';

export const FETCH_PAYOUT_STATUS_REQUEST = 'app/DashboardPage/FETCH_PAYOUT_STATUS_REQUEST';
export const FETCH_PAYOUT_STATUS_SUCCESS = 'app/DashboardPage/FETCH_PAYOUT_STATUS_SUCCESS';
export const FETCH_PAYOUT_STATUS_ERROR = 'app/DashboardPage/FETCH_PAYOUT_STATUS_ERROR';

export const WITHDRAWAL_REQUEST = 'app/DashboardPage/WITHDRAWAL_REQUEST';
export const WITHDRAWAL_SUCCESS = 'app/DashboardPage/WITHDRAWAL_SUCCESS';
export const WITHDRAWAL_ERROR = 'app/DashboardPage/WITHDRAWAL_ERROR';

const initialState = {
  fetchInProgress: false,
  fetchDashboardError: null,
  transactions: [],
  transactionRefs: [],
  activeDateRange: getDefaultDashboardDateRange(),
  loadedDateRange: null,
  walletTotalEarnings: null,
  availableAmount: new Money(0, 'USD'),
  withdrawalRequested: false,
  withdrawalError: null,
  fetchPayoutInProgress: false,
  fetchPayoutError: null,
  isPayoutEnabled: false,
  activeRequestId: null,
};

const entityRefs = entities =>
  (entities || []).map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const sortedTransactions = txs => {
  return reverse(
    sortBy(txs, tx => {
      return tx?.attributes?.lastTransitionedAt || tx?.attributes?.createdAt || null;
    })
  );
};

export const createDashboardSalesTransactionsQuery = ({ page = 1, startDate, endDate } = {}) => ({
  only: 'sale',
  page,
  perPage: 100,
  lastTransitions: INBOX_LAST_TRANSITIONS,
  ...getDashboardQueryDateParams({ startDate, endDate }),
  include: [
    'listing',
    'listing.images',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData',
    'metadata',
  ],
  'fields.listing': ['title', 'publicData', 'description'],
  'fields.user': USER_FIELDS,
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
});

export const getVisibleSalesTransactions = transactions =>
  sortedTransactions(
    filterVisibleTransactions((transactions || []).filter(isInboxVisibleTransaction))
  );

const withTransactions = (response, transactions) => ({
  ...response,
  data: {
    ...response?.data,
    data: transactions,
  },
});

const filterDashboardResponse = (response, context = {}) => {
  const sanitizedResponse = sanitizeTransactionQueryResponse(response);
  const rawTransactions = sanitizedResponse?.data?.data || [];
  let denormalizedTransactions = [];

  try {
    denormalizedTransactions = denormalisedResponseEntities(sanitizedResponse);
  } catch (e) {
    log.warn(e, 'dashboard-participant-denormalization-failed', context);
  }

  const { visibleTransactions, hiddenTransactions } = splitTransactionsByParticipantAvailability(
    rawTransactions,
    denormalizedTransactions,
    sanitizedResponse?.data?.included || []
  );

  hiddenTransactions.forEach(({ transaction, unavailableParticipant }) => {
    log.warn(
      createParticipantUnavailableError(),
      'dashboard-conversation-hidden-unavailable-participant',
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
  });

  return withTransactions(sanitizedResponse, visibleTransactions);
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_DASHBOARD_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchDashboardError: null,
        transactions: [],
        transactionRefs: [],
        availableAmount: new Money(0, 'USD'),
        activeDateRange: payload?.dateRange
          ? normalizeDashboardDateRange(payload.dateRange)
          : state.activeDateRange,
        activeRequestId: payload?.requestId ?? state.activeRequestId,
      };
    case FETCH_DASHBOARD_SUCCESS:
      if (
        payload?.requestId &&
        state.activeRequestId &&
        payload.requestId !== state.activeRequestId
      ) {
        return state;
      }

      return {
        ...state,
        fetchInProgress: false,
        fetchDashboardError: null,
        transactions: payload?.transactions || [],
        transactionRefs: entityRefs(payload?.transactions),
        loadedDateRange: payload?.dateRange
          ? normalizeDashboardDateRange(payload.dateRange)
          : state.loadedDateRange,
        walletTotalEarnings: payload?.walletTotalEarnings ?? state.walletTotalEarnings,
        availableAmount: payload.availableAmount,
        activeRequestId: null,
      };
    case FETCH_DASHBOARD_ERROR:
      if (
        payload?.requestId &&
        state.activeRequestId &&
        payload.requestId !== state.activeRequestId
      ) {
        return state;
      }

      return {
        ...state,
        fetchInProgress: false,
        fetchDashboardError: payload?.error || payload,
        activeRequestId: null,
      };
    case SET_DASHBOARD_TOTAL_EARNINGS:
      return {
        ...state,
        walletTotalEarnings: payload,
      };

    case FETCH_PAYOUT_STATUS_REQUEST:
      return {
        ...state,
        fetchPayoutInProgress: true,
        fetchPayoutError: null,
      };
    case FETCH_PAYOUT_STATUS_SUCCESS:
      return {
        ...state,
        fetchPayoutInProgress: false,
        isPayoutEnabled: payload,
      };
    case FETCH_PAYOUT_STATUS_ERROR:
      return {
        ...state,
        fetchPayoutInProgress: false,
        fetchPayoutError: payload,
      };

    case WITHDRAWAL_REQUEST:
      return {
        ...state,
        withdrawalRequested: true,
        withdrawalError: null,
      };
    case WITHDRAWAL_SUCCESS:
      return {
        ...state,
        withdrawalRequested: false,
        withdrawalError: null,
      };
    case WITHDRAWAL_ERROR:
      return {
        ...state,
        withdrawalRequested: false,
        withdrawalError: payload,
      };

    default:
      return state;
  }
}

const fetchDashboardRequest = payload => ({ type: FETCH_DASHBOARD_REQUEST, payload });
const fetchDashboardSuccess = payload => ({ type: FETCH_DASHBOARD_SUCCESS, payload });
const fetchDashboardError = payload => ({ type: FETCH_DASHBOARD_ERROR, payload, error: true });
const setDashboardTotalEarnings = payload => ({ type: SET_DASHBOARD_TOTAL_EARNINGS, payload });

const fetchPayoutStatusRequest = () => ({ type: FETCH_PAYOUT_STATUS_REQUEST });
const fetchPayoutStatusSuccess = payload => ({ type: FETCH_PAYOUT_STATUS_SUCCESS, payload });
const fetchPayoutStatusError = e => ({ type: FETCH_PAYOUT_STATUS_ERROR, payload: e, error: true });

const withdrawalRequest = () => ({ type: WITHDRAWAL_REQUEST });
const withdrawalSuccess = () => ({ type: WITHDRAWAL_SUCCESS });
const withdrawalError = e => ({ type: WITHDRAWAL_ERROR, payload: e, error: true });

const resolveDashboardTotalEarnings = async currentUserId => {
  if (!currentUserId) {
    return null;
  }

  try {
    const response = await queryTransaction({ userId: currentUserId });
    const totalPayoutAmount = response?.data?.totalPayoutAmount;
    return Number.isFinite(totalPayoutAmount) ? totalPayoutAmount / 100 : null;
  } catch (e) {
    if (!isNetworkError(e)) {
      log.error(e, 'dashboard-total-earnings-fetch-failed', {
        providerId: currentUserId,
      });
    }
    return null;
  }
};

export const fetchDashboardTransactions = (dateRange = getDefaultDashboardDateRange()) => async (
  dispatch,
  getState,
  sdk
) => {
  const normalizedDateRange = normalizeDashboardDateRange(dateRange);
  const dashboardState = getState()?.DashboardPage;
  const loadedDateRange = dashboardState?.loadedDateRange;

  if (
    dashboardState?.fetchInProgress &&
    isSameDashboardDateRange(dashboardState?.activeDateRange, normalizedDateRange)
  ) {
    return dashboardState?.transactions || [];
  }

  if (
    loadedDateRange &&
    !dashboardState?.fetchDashboardError &&
    isSameDashboardDateRange(loadedDateRange, normalizedDateRange)
  ) {
    return dashboardState?.transactions || [];
  }

  latestDashboardRequestId += 1;
  const requestId = latestDashboardRequestId;

  dispatch(fetchDashboardRequest({ requestId, dateRange: normalizedDateRange }));

  const MAX_RETRIES = 2;
  const queryWithRetry = async (params, retriesLeft) => {
    try {
      return await sdk.transactions.query(params);
    } catch (e) {
      if (retriesLeft > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(params, retriesLeft - 1);
      }
      throw e;
    }
  };

  try {
    const allTransactions = [];
    const firstResponse = await queryWithRetry(
      createDashboardSalesTransactionsQuery({ page: 1, ...normalizedDateRange }),
      MAX_RETRIES
    );
    const filteredFirstResponse = filterDashboardResponse(firstResponse, {
      page: 1,
      ...normalizedDateRange,
    });
    dispatch(addMarketplaceEntities(filteredFirstResponse));
    const firstData = filterVisibleTransactions(filteredFirstResponse?.data?.data || []);
    const totalPages = firstResponse?.data?.meta?.totalPages || 1;

    allTransactions.push(...firstData);

    for (let page = 2; page <= totalPages; page += 1) {
      const pagedResponse = await queryWithRetry(
        createDashboardSalesTransactionsQuery({ page, ...normalizedDateRange }),
        MAX_RETRIES
      );
      const filteredPagedResponse = filterDashboardResponse(pagedResponse, {
        page,
        ...normalizedDateRange,
      });
      dispatch(addMarketplaceEntities(filteredPagedResponse));
      const pagedData = filterVisibleTransactions(filteredPagedResponse?.data?.data || []);
      allTransactions.push(...pagedData);
    }

    const visibleTransactions = getVisibleSalesTransactions(allTransactions);

    const totalAmount = visibleTransactions.reduce((sum, tx) => sum + getNetPayoutAmount(tx), 0);

    dispatch(
      fetchDashboardSuccess({
        requestId,
        dateRange: normalizedDateRange,
        transactions: visibleTransactions,
        availableAmount: new Money(totalAmount, 'USD'),
      })
    );

    return visibleTransactions;
  } catch (e) {
    log.error(e, 'dashboard-wallet-transactions-fetch-failed', {
      transactionType: 'sale',
      ...normalizedDateRange,
    });
    dispatch(
      fetchDashboardError({
        requestId,
        error: storableError(e),
      })
    );
    return [];
  }
};

export const getStripeAccount = params => async dispatch => {
  dispatch(fetchPayoutStatusRequest());

  try {
    const response = await showStripeAccount(params);
    const isPayoutEnabled = !!response?.data?.isPayoutEnabled;
    dispatch(fetchPayoutStatusSuccess(isPayoutEnabled));
    return response;
  } catch (e) {
    log.error(e, 'dashboard-wallet-payout-status-fetch-failed', {
      stripeAccountId: params?.stripeAccountId || null,
    });
    dispatch(fetchPayoutStatusError(storableError(e)));
    return null;
  }
};

export const withdrawal = params => async dispatch => {
  dispatch(withdrawalRequest());

  try {
    const response = await createPayout(params);
    if (response?.payment === 'SUCCESS' || response?.statusCode === 200) {
      dispatch(withdrawalSuccess());
      dispatch(fetchCurrentUser());
      return response;
    }

    throw new Error('Failed to create payout');
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'dashboard-wallet-withdrawal-failed', {
      requestedWithdrawal: params?.withdrawal ?? null,
      stripeId: params?.stripeId || null,
    });
    dispatch(withdrawalError(error));
    return null;
  }
};

export const loadData = () => async (dispatch, getState) => {
  await dispatch(fetchCurrentUser());

  const currentUser = getState()?.user?.currentUser;
  const currentUserId = currentUser?.id?.uuid;
  const defaultDateRange = getDefaultDashboardDateRange();

  if (!currentUserId) {
    dispatch(
      fetchDashboardSuccess({
        dateRange: defaultDateRange,
        transactions: [],
        availableAmount: new Money(0, 'USD'),
      })
    );
    return null;
  }

  const stripeAccountId = currentUser?.stripeAccount?.attributes?.stripeAccountId;

  const [walletTotalEarnings] = await Promise.all([
    resolveDashboardTotalEarnings(currentUserId),
    dispatch(fetchDashboardTransactions(defaultDateRange)),
    stripeAccountId ? dispatch(getStripeAccount({ stripeAccountId })) : Promise.resolve(null),
  ]);

  if (walletTotalEarnings !== null) {
    dispatch(setDashboardTotalEarnings(walletTotalEarnings));
  }

  return null;
};
